import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getGroup,
  getGroups,
  createGroup,
  updateGroup,
} from '../api/groups.api';
import {
  IGroup,
  IGetGroup,
  IGetGroups,
  IGroupReq,
} from '../models/groups.model';

interface IGroupState {
  group?: IGroup;
  groups?: IGroup[];
  isLoading: boolean;
  error?: AxiosError | string;
}

const initialState: IGroupState = {
  group: undefined,
  groups: [],
  isLoading: false,
};

const groupSlicer = createSlice({
  name: 'group',
  initialState,
  reducers: {
    getGroupStart: (state: IGroupState) => {
      state.group = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getGroupSuccess: (state: IGroupState, action: PayloadAction<IGetGroup>) => {
      state.group = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getGroupFailure: (
      state: IGroupState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.group = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getGroupsStart: (state: IGroupState) => {
      state.groups = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getGroupsSuccess: (
      state: IGroupState,
      action: PayloadAction<IGetGroups>,
    ) => {
      state.groups = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getGroupsFailure: (
      state: IGroupState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.groups = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: IGroupState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: IGroupState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: IGroupState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: IGroupState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  },
});

export const {
  getGroupStart,
  getGroupSuccess,
  getGroupFailure,
  getGroupsStart,
  getGroupsSuccess,
  getGroupsFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess,
} = groupSlicer.actions;

export default groupSlicer.reducer;

export const doGetGroup =
  (id: number): any =>
  async (dispatch: any) => {
    try {
      dispatch(getGroupStart());
      const response = await getGroup(id);
      dispatch(getGroupSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getGroupFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetGroups = (): any => async (dispatch: any) => {
  try {
    dispatch(getGroupsStart());
    const response = await getGroups();
    dispatch(getGroupsSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getGroupsFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateGroup =
  (data: IGroupReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createGroup(data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateGroup =
  (id: number, data: IGroupReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateGroup(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
